import { Component, signal, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Hub } from 'aws-amplify/utils';
import { AuthService } from '../auth/auth.service';
import { ThemeService } from '../theme/theme.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
})
export class LoginComponent implements OnInit, OnDestroy {
  loading = signal(false);
  error = signal<string | null>(null);
  showNewPasswordForm = signal(false);
  redirectingToHostedUi = signal(false);
  form: FormGroup;
  newPasswordForm: FormGroup;
  readonly themeService = inject(ThemeService);
  private hubUnsubscribe: (() => void) | null = null;

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.form = this.fb.group({
      username: ['', [Validators.required]],
      password: ['', [Validators.required]],
    });
    this.newPasswordForm = this.fb.group({
      newPassword: ['', [Validators.required, Validators.minLength(8), this.cognitoPasswordPolicyValidator]],
      confirmPassword: ['', [Validators.required]],
    }, { validators: this.passwordMatchValidator });
  }

  /** Matches Castor Cognito pool policy: min 8 chars, upper, lower, number. */
  private cognitoPasswordPolicyValidator = (control: AbstractControl): ValidationErrors | null => {
    const v = control.value;
    if (!v || v.length < 8) return null; // minLength handles length
    const hasLower = /[a-z]/.test(v);
    const hasUpper = /[A-Z]/.test(v);
    const hasNumber = /[0-9]/.test(v);
    return hasLower && hasUpper && hasNumber ? null : { cognitoPolicy: true };
  };

  private passwordMatchValidator(g: FormGroup): { mismatch: boolean } | null {
    const newPassword = g.get('newPassword')?.value;
    const confirm = g.get('confirmPassword')?.value;
    return newPassword && confirm && newPassword !== confirm ? { mismatch: true } : null;
  }

  ngOnInit(): void {
    this.hubUnsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          this.router.navigate(['/customers']);
          break;
        case 'signInWithRedirect_failure':
          this.redirectingToHostedUi.set(false);
          this.error.set((payload.data as { error?: { message?: string } })?.error?.message ?? 'OAuth sign-in failed');
          break;
      }
    });

    const fromInstitucional = this.route.snapshot.queryParamMap.get('from') === 'institucional';
    const hasOAuthCode = this.route.snapshot.queryParamMap.has('code');
    if (fromInstitucional && !hasOAuthCode) {
      this.redirectingToHostedUi.set(true);
      this.auth.signInWithRedirect();
    }
  }

  ngOnDestroy(): void {
    this.hubUnsubscribe?.();
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  async submit(): Promise<void> {
    if (this.form.invalid || this.loading()) return;
    this.error.set(null);
    this.loading.set(true);
    const { username, password } = this.form.value as { username: string; password: string };
    const res = await this.auth.signIn(username, password);
    this.loading.set(false);
    if (res.isSignedIn) {
      this.router.navigate(['/customers']);
    } else if (res.requiresNewPassword) {
      this.showNewPasswordForm.set(true);
    } else {
      this.error.set(res.error ?? 'Could not sign in');
    }
  }

  backToLogin(): void {
    this.showNewPasswordForm.set(false);
    this.error.set(null);
    this.newPasswordForm.reset();
  }

  async submitNewPassword(): Promise<void> {
    if (this.newPasswordForm.invalid || this.loading()) return;
    this.error.set(null);
    this.loading.set(true);
    const newPassword = this.newPasswordForm.get('newPassword')?.value as string;
    const res = await this.auth.confirmSignInWithNewPassword(newPassword);
    this.loading.set(false);
    if (res.isSignedIn) {
      this.router.navigate(['/customers']);
    } else {
      this.error.set(res.error ?? 'Could not set password');
    }
  }
}
